import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Category {
  id: number
  name: string
}

interface Props {
  report: {
    name: string
    link: string
    description: string
    notes: string
    internal_notes: string
    status: number
    admin_only: boolean
    category_ids: number[]
  }
  categories: Category[]
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/reports" },
  { title: "Reports", href: "/admin/reports" },
  { title: "Add", href: "/admin/reports/new" },
]

export default function New({ report, categories, errors }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: report.name,
    link: report.link,
    description: report.description,
    notes: report.notes,
    internal_notes: report.internal_notes,
    admin_only: report.admin_only,
    category_ids: report.category_ids,
  })

  transform((data) => ({ report: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/admin/reports")
  }

  const toggleCategory = (id: number) => {
    setData(
      "category_ids",
      data.category_ids.includes(id)
        ? data.category_ids.filter((cid) => cid !== id)
        : [...data.category_ids, id],
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Report" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Report</h1>

        <form onSubmit={handleSubmit} className="max-w-lg">
          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block font-medium">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="link" className="mb-1 block font-medium">
              Link
            </label>
            <input
              type="text"
              id="link"
              value={data.link}
              onChange={(e) => setData("link", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={200}
            />
            {errors?.link && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.link.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label className="mb-1 block font-medium">Categories</label>
            <div className="border-hpi-light-gray max-h-40 overflow-y-auto rounded border p-2">
              {categories.map((c) => (
                <label key={c.id} className="flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    checked={data.category_ids.includes(c.id)}
                    onChange={() => toggleCategory(c.id)}
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="description" className="mb-1 block font-medium">
              Description
            </label>
            <textarea
              id="description"
              value={data.description}
              onChange={(e) => setData("description", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="notes" className="mb-1 block font-medium">
              Notes
            </label>
            <textarea
              id="notes"
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="internal_notes" className="mb-1 block font-medium">
              Internal Notes
            </label>
            <textarea
              id="internal_notes"
              value={data.internal_notes}
              onChange={(e) => setData("internal_notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.admin_only}
                onChange={(e) => setData("admin_only", e.target.checked)}
              />
              <span className="font-medium">Admin Only</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Add
            </button>
            <Link
              href="/admin/reports"
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
